import styled, { css } from 'styled-components';

import { Button, IconButton } from '@xing-com/button';
import { IconArrowLeft } from '@xing-com/icons';
import { mediaConfined, mediaSmallOrTiny } from '@xing-com/layout-tokens';
import { Marker as MarkerComponent } from '@xing-com/marker';
import { ModalPage } from '@xing-com/modal-page';
import { InputBar } from '@xing-com/text-field';
import {
  spaceXL,
  xdlPaletteLime03,
  xdlPaletteLime07,
  xdlColorBackground,
  scale210,
  scale220,
  spaceM,
  scale170,
  scale180,
  spaceS,
  cornerRadiusXL,
  scale070,
  scale080,
  spaceXXL,
  spaceXS,
  spaceXXS,
  xdlPaletteGrayCool11,
  xdlPaletteGrayCool09,
} from '@xing-com/tokens';
import { BodyCopy, Headline, Hero } from '@xing-com/typography';

import { MainIllustration } from './assets/main-illustration';
import { StickerFlashFixedStroke } from './assets/sticker-flash-fixed-stroke';

const HEADER_HEIGHT_SM = scale170;
const BOTTOM_BAR_HEIGHT = scale180;

const background = css`
  background: radial-gradient(
    60% 60% at 50% 60%,
    ${xdlPaletteLime03} 0%,
    ${xdlPaletteLime07} 100%
  );

  @media ${mediaConfined} {
    background: radial-gradient(
      30% 60% at 50% 30%,
      ${xdlPaletteLime03} 0%,
      ${xdlPaletteLime07} 100%
    );
  }

  @media (prefers-color-scheme: dark) {
    background: radial-gradient(
      60% 60% at 50% 60%,
      ${xdlPaletteGrayCool09} 0%,
      ${xdlPaletteGrayCool11} 100%
    );

    @media ${mediaConfined} {
      background: radial-gradient(
        30% 60% at 50% 30%,
        ${xdlPaletteGrayCool09} 0%,
        ${xdlPaletteGrayCool11} 100%
      );
    }
  }
`;

const betweenSectionsGradient = css`
  @media ${mediaConfined} {
    padding-bottom: 395px;

    &::after {
      content: '';
      pointer-events: none;
      position: absolute;
      width: 100%;
      height: 100%;
      left: 0px;
      bottom: 0px;
      background: radial-gradient(
        175% 45% at 50% 100%,
        ${xdlColorBackground} 25%,
        transparent 100%
      );
    }
  }
`;

export const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  text-wrap: balance;
  padding: ${spaceXL} 20px;

  ${background}
  ${betweenSectionsGradient}

  @media ${mediaSmallOrTiny} {
    min-height: calc(
      100dvh - ${HEADER_HEIGHT_SM} - ${BOTTOM_BAR_HEIGHT} - ${spaceXXL}
    );
  }
`;

export const IllustrationVariantB = styled(MainIllustration)`
  flex-shrink: 0;
  margin-bottom: ${spaceXL};

  @media ${mediaConfined} {
    margin-bottom: ${spaceM};
  }
`;

export const HeadlineVariantB = styled(Headline).attrs({
  size: 'xxlarge',
  forwardedAs: 'label',
})`
  margin-top: ${spaceS};
  margin-bottom: ${spaceXXL};

  @media ${mediaConfined} {
    display: none;
  }
`;

export const HeroVariantB = styled(Hero).attrs({ forwardedAs: 'label' })`
  margin-top: ${spaceXS};
  margin-bottom: ${spaceXXL};

  @media ${mediaSmallOrTiny} {
    display: none;
  }
`;

export const RotatedContainer = styled.div`
  transform: rotate(-6deg);

  @media ${mediaConfined} {
    transform: rotate(-4deg);
  }
`;

export const Marker = styled(MarkerComponent).attrs({ variant: 'ai' })`
  text-transform: uppercase;
`;

export const BodyCopyVariantC = styled(BodyCopy).attrs({ size: 'small' })`
  margin-top: ${spaceS};
  margin-bottom: ${spaceXXS};

  @media ${mediaConfined} {
    margin-top: ${spaceXS};
  }
`;

export const HeroVariantC = styled(Hero).attrs({ size: 'large' })`
  display: flex;
  align-items: center;
  margin-bottom: 0;
  font-size: ${scale170};
  line-height: 52px;
  letter-spacing: -1px;

  @media ${mediaSmallOrTiny} {
    word-spacing: 100vw;
  }
`;

const IllustrationVariantC = styled(StickerFlashFixedStroke)`
  flex-shrink: 0;
`;

export const InlineIllustrationVariantC = styled(IllustrationVariantC)`
  position: absolute;
  right: -57px;

  @media ${mediaSmallOrTiny} {
    display: none;
  }
`;

export const SmallIllustrationVariantC = styled(IllustrationVariantC)`
  @media ${mediaConfined} {
    display: none;
  }
`;

export const LabelVariantC = styled(BodyCopy).attrs({
  sizeConfined: 'xlarge',
  forwardedAs: 'label',
})`
  margin-top: ${spaceXL};
  margin-bottom: ${spaceS};
`;

export const Modal = styled(ModalPage)`
  ${background}

  && {
    margin: 0;
  }
`;

export const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${spaceM};
  padding: ${spaceM} 20px ${spaceXL} 20px;
`;

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spaceXXL};
  width: 100%;
`;

export const BackButton = styled(IconButton).attrs({
  icon: IconArrowLeft,
})`
  align-self: start;
`;

export const InputWrapper = styled.label<{ $show: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spaceS};
  width: 100%;

  /**
   * Animation
   * https://codepen.io/silkine/pen/wBMyLLy/df8cd30923a9d7c14ab52c693de6d0ea
   */
  transition:
    max-height 0.35s ease,
    opacity 0.25s ease,
    transform 0.35s ease;

  ${({ $show }) =>
    $show
      ? css`
          max-height: 116px;
          opacity: 1;
          transform: translateY(0);

          @media ${mediaConfined} {
            max-height: 119px;
          }
        `
      : css`
          max-height: 0;
          opacity: 0;
          transform: translateY(-6px);
          pointer-events: none;
        `};
`;

export const Input = styled(InputBar).attrs({
  variant: 'plain',
  size: 'xlarge',
})<{ $isCursorHidden?: boolean }>`
  max-width: 640px;
  width: 100%;

  & input {
    height: ${scale210};
    border-radius: ${scale220};

    &::placeholder {
      font-size: ${scale080};
    }

    @media ${mediaSmallOrTiny} {
      font-size: ${scale080};

      &::placeholder {
        font-size: ${scale070};
      }
    }

    ${({ $isCursorHidden }) =>
      $isCursorHidden &&
      css`
        caret-color: transparent;

        &::selection {
          background: transparent;
        }
      `};
  }

  & button {
    inset-block-start: 22px;
  }
`;

export const SubmitButton = styled(Button).attrs({
  variant: 'onColorStrong',
  size: 'large',
})<{ $showCompanyInput: boolean }>`
  ${({ $showCompanyInput }) =>
    !$showCompanyInput &&
    css`
      margin-top: -${spaceXXL};
    `};
`;

export const NextSectionTop = styled.div`
  margin: -${scale080} 0;
  height: calc(${scale080} + 1px);
  background: ${xdlColorBackground};
  border-top-left-radius: ${cornerRadiusXL};
  border-top-right-radius: ${cornerRadiusXL};
  box-shadow: 0px -8px 14px 2px rgba(33, 42, 54, 0.08);
  clip-path: inset(-28px 0px 0px 0px);

  @media ${mediaConfined} {
    display: none;
  }
`;
